/** Libraries * */
import React from "react";
import get from "lodash/get";
import noop from "lodash/noop";
import classNames from "classnames";
import PropTypes from "prop-types";
import {useTranslation} from "@unibuddy/intl";

/** custom components * */
import {ResponsiveEmbed} from "../../../../components/ResponsiveEmbed/ResponsiveEmbed";
import NumberOfViews from "../NumberOfViews/NumberOfViews";
import {ThemedLink} from "../../../../components/ThemedLink/ThemedLink";
import BlogPostPreviewAuthor from "../BlogPostPreviewAuthor/BlogPostPreviewAuthor";
import Carousel from "../../../General/components/Carousel/Carousel";

/** styles * */
import s from "./BlogPostPreviewCard.pcss";
import BlogPostTextPreview from "../BlogPostTextPreview/BlogPostTextPreview";
import BlogImage from "../BlogImage/BlogImage";
import {IntlNamespaces} from "../../../../translations/types";
import {INTL_FORMER_AMBASSADOR} from "../../../../translations/common";
import {INTL_MINUTE_READ} from "../../../../translations/blogs";

const BlogPostPreviewCard = (props) => {
    const shouldLinkToProfile =
        get(props, "post.author.active", false) && !get(props, "post.author.archived", false);
    const {t} = useTranslation([IntlNamespaces.BLOGS, IntlNamespaces.COMMON]);
    const coverImages = get(props, "post.coverImages");
    const firstName = props?.post?.author?.archived
        ? t(
              `${IntlNamespaces.COMMON}:${INTL_FORMER_AMBASSADOR.key}`,
              INTL_FORMER_AMBASSADOR.defaultValue,
          )
        : props?.post?.author?.firstName;

    return (
        <div className={classNames(s.container, "post-preview")}>
            {props.showAuthor ? (
                <BlogPostPreviewAuthor
                    profilePhoto={get(props, "post.author.profilePhoto")}
                    authorUrl={props.authorUrl}
                    firstName={firstName}
                    degreeName={get(props, "post.author.degree.name")}
                    shouldLinkToProfile={shouldLinkToProfile}
                    onSelectAuthor={props.onSelectAuthor}
                />
            ) : null}
            <ThemedLink onClick={() => props.onSelectPost(props.post.id)} to={props.postUrl}>
                {coverImages?.length > 0 && (
                    <div className={s.imageContainer}>
                        <Carousel autoplay infinite arrows={false} dots>
                            {coverImages.map((image, idx) => (
                                <BlogImage
                                    // eslint-disable-next-line react/no-array-index-key
                                    // key={`blog-preview-image-${props.post.id}-${idx}`}
                                    id={`blog-preview-image-${props.post.id}-${idx}`}
                                    image={image}
                                    width={516}
                                    height={290}
                                />
                            ))}
                        </Carousel>
                    </div>
                )}
                {props.post.youtubeVideo && (
                    <div className={s.videoContainer}>
                        <ResponsiveEmbed>
                            {/* eslint-disable-next-line jsx-a11y/iframe-has-title */}
                            <iframe
                                title={props.post.title}
                                src={`https://www.youtube-nocookie.com/embed/${props.post.youtubeVideo}`}
                            />
                        </ResponsiveEmbed>
                    </div>
                )}
                <div className={s.title}>{props.post.title}</div>
            </ThemedLink>
            <div className={s.content}>
                <BlogPostTextPreview body={get(props, "post.body")} />
            </div>
            <div className={s.info}>
                <div className={s.topic} data-test-id={"read-time"}>
                    {props.post.topic.name} · {props.post.readingTime}{" "}
                    {t(
                        `${IntlNamespaces.BLOGS}:${INTL_MINUTE_READ.key}`,
                        INTL_MINUTE_READ.defaultValue,
                    )}
                </div>
                <div className={s.numberOfViews}>
                    <NumberOfViews count={props.post.numberOfViews} />
                </div>
            </div>
        </div>
    );
};

BlogPostPreviewCard.propTypes = {
    post: PropTypes.shape({
        image: PropTypes.string,
        youtubeVideo: PropTypes.string,
        author: PropTypes.object.isRequired,
        topic: PropTypes.object.isRequired,
    }).isRequired,
    onSelectPost: PropTypes.func,
    onSelectAuthor: PropTypes.func,
    postUrl: PropTypes.string.isRequired,
    authorUrl: PropTypes.string,
    showAuthor: PropTypes.bool,
};

BlogPostPreviewCard.defaultProps = {
    authorUrl: "",
    showAuthor: false,
    onSelectAuthor: noop,
    onSelectPost: noop,
};

export default BlogPostPreviewCard;
